import {
  REPORT_SECTION_LABELS,
  ReportDocument,
  type ReportDataModelV1,
  type ReportSectionKey,
  type ReportSectionSummary,
} from "@safeplate/report";
import { Button, Card, CardContent } from "@safeplate/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Download, ExternalLink, FileText } from "lucide-react";
import { useEffect, useState } from "react";

import { apiBaseUrl } from "../api/client.js";
import { ErrorState, LoadingState } from "../components/states.js";

interface PublicReportResponse {
  report: Omit<ReportDataModelV1, "photos" | "missing_evidence"> & {
    photos: {
      included: boolean;
      items: { caption: string; record_ref: string; display_url: string }[];
    };
  };
  sections: ReportSectionSummary[];
}

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: "omit",
    cache: "no-store",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(body?.error?.message ?? "The report is unavailable");
  }
  return response.json() as Promise<T>;
}

function reportDate(value: string) {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(new Date(`${value}T12:00:00Z`));
}

export function PublicReportPage({
  token,
  slug,
  period,
  section,
}: {
  token: string;
  slug: string;
  period: string;
  section?: string;
}) {
  const [pdfJob, setPdfJob] = useState<{
    id: string;
    mode: "inline" | "attachment";
  } | null>(null);
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex,nofollow,noarchive";
    document.head.append(robots);
    const referrer = document.createElement("meta");
    referrer.name = "referrer";
    referrer.content = "no-referrer";
    document.head.append(referrer);
    return () => {
      robots.remove();
      referrer.remove();
    };
  }, []);
  const report = useQuery({
    queryKey: ["public-report", token],
    queryFn: () =>
      publicRequest<PublicReportResponse>(`/public/report-shares/${token}`),
    staleTime: Infinity,
    retry: false,
  });
  const startPdf = useMutation({
    mutationFn: (mode: "inline" | "attachment") =>
      publicRequest<{ id: string }>(`/public/report-shares/${token}/pdf`, {
        method: "POST",
      }).then((job) => ({ ...job, mode })),
    onSuccess: setPdfJob,
  });
  const pdf = useQuery({
    queryKey: ["public-report-pdf", token, pdfJob?.id],
    enabled: Boolean(pdfJob),
    queryFn: () =>
      publicRequest<{
        status: string;
        progress_pct: number;
        error: string | null;
      }>(`/public/report-shares/${token}/pdf/${pdfJob!.id}`),
    refetchInterval: (query) =>
      ["queued", "building", "rendering"].includes(
        query.state.data?.status ?? "queued",
      )
        ? 3000
        : false,
  });
  const selected = report.data?.sections.find((item) => item.key === section);
  const rdm = report.data?.report
      ? ({
        ...report.data.report,
        missing_evidence: [],
        photos: {
          ...report.data.report.photos,
          items: report.data.report.photos.items.map((photo, index) => ({
            ...photo,
            attachment_id: String(index),
            object_key: "",
          })),
        },
      } satisfies ReportDataModelV1)
    : null;
  if (report.isPending)
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <LoadingState label="Opening shared report" />
      </main>
    );
  if (report.error || !rdm)
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <ErrorState
          title="This report link isn’t available"
          message={
            report.error instanceof Error
              ? report.error.message
              : "The link may have been revoked."
          }
          onAction={() => void report.refetch()}
        />
      </main>
    );
  if (section && !selected)
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <div>
          <h1 className="text-xl font-semibold">Section not found</h1>
          <Link
            className="mt-3 inline-block text-emerald-800 underline"
            to="/share/$slug/report/$period/$token"
            params={{ slug, period, token }}
          >
            See all sections
          </Link>
        </div>
      </main>
    );

  return (
    <main className="min-h-dvh bg-canvas text-stone-950">
      <header className="border-b border-stone-200 bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link
            to="/share/$slug/report/$period/$token"
            params={{ slug, period, token }}
            className="flex items-center gap-3 text-stone-950 no-underline"
          >
            <span className="grid size-9 place-items-center rounded-xl bg-emerald-900 text-sm font-black text-white">
              SP
            </span>
            <span className="text-lg font-bold">SafePlate</span>
          </Link>
          <span className="text-xs text-stone-500">Shared evidence report</span>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-8 sm:py-12">
        {selected ? (
          <Link
            className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:underline"
            to="/share/$slug/report/$period/$token"
            params={{ slug, period, token }}
          >
            <ArrowLeft className="size-4" /> All sections
          </Link>
        ) : null}
        <div className="mb-7 flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-xs font-semibold tracking-widest text-emerald-800 uppercase">
              {selected ? selected.label : "Evidence report"}
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
              {rdm.business.location_name}
            </h1>
            <p className="mt-2 text-sm text-stone-500">
              {reportDate(rdm.period.start)} – {reportDate(rdm.period.end)} ·
              Updated when you reload this page
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => startPdf.mutate("inline")}
              disabled={startPdf.isPending}
            >
              <ExternalLink className="size-4" /> View PDF
            </Button>
            <Button
              onClick={() => startPdf.mutate("attachment")}
              disabled={startPdf.isPending}
            >
              <Download className="size-4" /> Download PDF
            </Button>
          </div>
        </div>
        {startPdf.error ? (
          <p role="alert" className="mb-5 text-sm text-red-800">
            {startPdf.error.message}
          </p>
        ) : null}
        {pdfJob && pdf.data?.status !== "completed" ? (
          <Card className="mb-5">
            <CardContent className="p-4 text-sm">
              {pdf.data?.status === "failed"
                ? `PDF generation failed: ${pdf.data.error ?? "Please try again"}`
                : `Preparing the latest PDF… ${pdf.data?.progress_pct ?? 0}%`}
            </CardContent>
          </Card>
        ) : null}
        {pdfJob && pdf.data?.status === "completed" ? (
          <Card className="mb-5">
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
              <span>Your current PDF is ready.</span>
              <a
                className="font-semibold text-emerald-800 underline"
                target={pdfJob.mode === "inline" ? "_blank" : undefined}
                rel="noreferrer"
                href={`${apiBaseUrl}/public/report-shares/${token}/pdf/${pdfJob.id}/file?disposition=${pdfJob.mode}`}
              >
                {pdfJob.mode === "inline" ? "Open PDF" : "Save PDF"}
              </a>
            </CardContent>
          </Card>
        ) : null}
        {selected ? (
          <Card>
            <CardContent className="overflow-x-auto p-5 sm:p-8">
              <ReportDocument
                presentation="web"
                rdm={rdm}
                sectionKeys={[selected.key as ReportSectionKey]}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {report.data!.sections.map((item) => (
              <Link
                key={item.key}
                to="/share/$slug/report/$period/$token/$section"
                params={{ slug, period, token, section: item.key }}
                className="group block no-underline"
              >
                <Card className="h-full transition-colors group-hover:border-emerald-500">
                  <CardContent className="flex h-full flex-col p-5">
                    <FileText className="size-5 text-emerald-800" />
                    <h2 className="mt-5 text-lg font-semibold text-stone-950">
                      {REPORT_SECTION_LABELS[item.key]}
                    </h2>
                    <p className="mt-2 text-sm text-stone-500">
                      {item.count} {item.count === 1 ? "entry" : "entries"} in
                      this period
                    </p>
                    <span className="mt-6 text-sm font-semibold text-emerald-800">
                      View section →
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
